import { convertCo2Score } from '@/data/http/utils/convert-co2-score';
import {
  CalculateTourCo2,
  CalculateTourGeneralCo2,
  CalculateTourGlobalCo2,
  CalculateTourOntourCo2,
  EMPTY_CALCULATE_TOUR_CO2,
  EMPTY_CALCULATE_TOUR_CO2_VARIOUS_COST,
} from '@/domain/impact-calculate-tour/entities/calculate-tour-co2.entity';

import { ResultsTourDto } from '../dto.type';

export function mapCo2(dto: ResultsTourDto): CalculateTourCo2 {
  return {
    global: mapCo2Global(dto),
    general: mapCo2General(dto),
    ontour: mapCo2Ontour(dto),
  };
}

function mapCo2Global(dto: ResultsTourDto): CalculateTourGlobalCo2 {
  return {
    score: convertCo2Score(
      dto?.global?.co2?.score || EMPTY_CALCULATE_TOUR_CO2.global.score,
    ),
  };
}

function mapCo2General(dto: ResultsTourDto): CalculateTourGeneralCo2 {
  if (!dto?.general?.co2) {
    return EMPTY_CALCULATE_TOUR_CO2.general;
  }
  return {
    score: convertCo2Score(dto.general.co2.score),
    sections: {
      CREATION: {
        score: convertCo2Score(dto.general.co2.sections.CREATION.score),
        categories: {
          SCENOGRAPHY: convertScoreObject(
            dto.general.co2.sections.CREATION.categories.SCENOGRAPHY,
          ),
          TEAM_MOBILITY: convertScoreObject(
            dto.general.co2.sections.CREATION.categories.TEAM_MOBILITY,
          ),
          REPEATS_AND_MATERIAL: convertScoreObject(
            dto.general.co2.sections.CREATION.categories.REPEATS_AND_MATERIAL,
          ),
        },
      },
      MANAGEMENT: {
        score: convertCo2Score(dto.general.co2.sections.MANAGEMENT.score),
        categories: {
          TEAM_MOBILITY: convertScoreObject(
            dto.general.co2.sections.MANAGEMENT.categories.TEAM_MOBILITY,
          ),
          CATERING: convertScoreObject(
            dto.general.co2.sections.MANAGEMENT.categories.CATERING,
          ),
          OFFICE: convertScoreObject(
            dto.general.co2.sections.MANAGEMENT.categories.OFFICE,
          ),
        },
      },
      COMMUNICATION: {
        score: convertCo2Score(dto.general.co2.sections.COMMUNICATION.score),
        categories: {
          MERCHANDISING: convertScoreObject(
            dto.general.co2.sections.COMMUNICATION.categories.MERCHANDISING,
          ),
          ADVERTISING: convertScoreObject(
            dto.general.co2.sections.COMMUNICATION.categories.ADVERTISING,
          ),
          VIDEO_AND_DIGITAL: convertScoreObject(
            dto.general.co2.sections.COMMUNICATION.categories.VIDEO_AND_DIGITAL,
          ),
          TICKETING: convertScoreObject(
            dto.general.co2.sections.COMMUNICATION.categories.TICKETING,
          ),
        },
      },
    },
  };
}

function mapCo2Ontour(dto: ResultsTourDto): CalculateTourOntourCo2 {
  return {
    score: convertCo2Score(
      dto.ontour.co2?.score || EMPTY_CALCULATE_TOUR_CO2.ontour.score,
    ),
    bySequence: convertSequences(dto.ontour.sequences),
    byTourEvent: convertEvents(dto.ontour.events),
    byTourTravel: convertTravels(dto.ontour.travels),
  };
}

function convertSequences(
  dto: ResultsTourDto['ontour']['sequences'],
): CalculateTourOntourCo2['bySequence'] {
  const result = dto.reduce<CalculateTourOntourCo2['bySequence']>(
    (acc, cur) => {
      acc[cur.id] = {
        score: convertCo2Score(cur.results.co2.score),
        events: convertCo2Score(cur.results.co2.events),
        travels: convertCo2Score(cur.results.co2.travels),
        variousCost: cur.results.co2.variousCost
          ? {
              score: convertCo2Score(cur.results.co2.variousCost?.score || 0),
              sectors: {
                artists: {
                  categories: {
                    accommodation: convertScoreObject(
                      cur.results.co2.variousCost.sectors.artists.categories
                        .accommodation,
                    ),
                    catering: convertScoreObject(
                      cur.results.co2.variousCost.sectors.artists.categories
                        .catering,
                    ),
                    equipment: convertScoreObject(
                      cur.results.co2.variousCost.sectors.artists.categories
                        .equipment,
                    ),
                  },
                },
              },
            }
          : EMPTY_CALCULATE_TOUR_CO2_VARIOUS_COST,
      };
      return acc;
    },
    {},
  );
  return result;
}

function convertEvents(
  dto: ResultsTourDto['ontour']['events'],
): CalculateTourOntourCo2['byTourEvent'] {
  const result = dto.reduce<CalculateTourOntourCo2['byTourEvent']>((acc, x) => {
    return { ...acc, [x.id]: { score: convertCo2Score(x.results.co2.score) } };
  }, {});
  return result;
}

function convertTravels(
  dto: ResultsTourDto['ontour']['travels'],
): CalculateTourOntourCo2['byTourTravel'] {
  const result = dto.reduce<CalculateTourOntourCo2['byTourTravel']>(
    (acc, cur) => {
      acc[cur.id] = {
        score: convertCo2Score(cur.results.co2?.score || 0),
      };
      return acc;
    },
    {},
  );
  return result;
}

function convertScoreObject(score: { score: number }) {
  return { score: convertCo2Score(score.score) };
}
